/**
 * BoardSurface — the bare board: a `BoardCanvas` wired to the surrounding
 * `BoardProvider`. No chrome. This is the composable primitive — compose it alone
 * for a chrome-less embed, or beside `BoardChromeSlot` for the full board.
 *
 * It subscribes to the STABLE `BoardCommands` context only (root, the viewport
 * report, the canvas registration, the operator flags) — never the volatile focal
 * context — so a breadcrumb / focal change does not re-render it. Everything
 * board-local that chrome never shares (mobile, className, initialViewport,
 * selection, minimap config, `interactive`, …) stays a prop.
 */
import { BoardCanvas, type BoardCanvasProps } from "./BoardCanvas";
import { useBoardCommands } from "./BoardContext";
import { registerBuiltinWidgets } from "./builtinWidgets";

// The bare board owns widget registration (arch-module, …): register eagerly at
// module load — BEFORE any board renders — so node tags mount already-upgraded.
// Any composition that uses a BoardSurface (embed, catalog, or InfiniteBoard) is
// covered; idempotent + SSR-guarded, so InfiniteBoard's own call is harmless.
if (typeof window !== "undefined") registerBuiltinWidgets();

/** Board-render config minus what the substrate supplies. */
export type BoardSurfaceProps = Omit<
  BoardCanvasProps,
  "root" | "onViewportChange" | "operatorEnabled" | "operatorInteractive"
>;

export function BoardSurface(props: BoardSurfaceProps) {
  const { root, reportViewport, registerCanvas, operatorEnabled, operatorInteractive } = useBoardCommands();
  return (
    <BoardCanvas
      {...props}
      ref={registerCanvas}
      root={root}
      onViewportChange={reportViewport}
      operatorEnabled={operatorEnabled}
      operatorInteractive={operatorInteractive}
    />
  );
}
